'use client';

import React, { useState } from 'react';
import { Download, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import PageKPICards from './PageKPICards';
import EngagementChart from './EngagementChart';
import AudienceDemographicsChart from './AudienceDemographicsChart';
import TopPostsTable from './TopPostsTable';
import Badge from '@/components/ui/Badge';

export interface PageData {
  id: string;
  client: string;
  pageName: string;
  platform: 'facebook' | 'instagram';
  followers: number;
  followerGrowth: number;
  followerGrowthPct: number;
  reach7d: number;
  impressions7d: number;
  engagementRate: number;
  avgLikes: number;
  avgComments: number;
  avgShares: number;
}

const pages: PageData[] = [
  { id: 'page-001', client: 'Bravo Coffee', pageName: 'Bravo Coffee Official', platform: 'facebook', followers: 48200, followerGrowth: 312, followerGrowthPct: 0.65, reach7d: 124000, impressions7d: 318000, engagementRate: 4.2, avgLikes: 284, avgComments: 38, avgShares: 52 },
  { id: 'page-002', client: 'Bravo Coffee', pageName: 'Bravo Coffee IG', platform: 'instagram', followers: 62800, followerGrowth: 891, followerGrowthPct: 1.44, reach7d: 198000, impressions7d: 440000, engagementRate: 6.8, avgLikes: 1240, avgComments: 87, avgShares: 0 },
  { id: 'page-003', client: 'Nexus Tech', pageName: 'Nexus Technology', platform: 'facebook', followers: 31400, followerGrowth: 148, followerGrowthPct: 0.47, reach7d: 88000, impressions7d: 210000, engagementRate: 2.9, avgLikes: 142, avgComments: 24, avgShares: 31 },
  { id: 'page-004', client: 'Verde Organic', pageName: 'Verde Organic Foods', platform: 'instagram', followers: 89600, followerGrowth: 1240, followerGrowthPct: 1.40, reach7d: 284000, impressions7d: 610000, engagementRate: 7.4, avgLikes: 2180, avgComments: 124, avgShares: 0 },
  { id: 'page-005', client: 'Luxe Living', pageName: 'Luxe Living Interiors', platform: 'instagram', followers: 41200, followerGrowth: 420, followerGrowthPct: 1.03, reach7d: 142000, impressions7d: 320000, engagementRate: 5.1, avgLikes: 680, avgComments: 42, avgShares: 0 },
  { id: 'page-006', client: 'UrbanWear Co.', pageName: 'UrbanWear Co.', platform: 'instagram', followers: 118400, followerGrowth: -240, followerGrowthPct: -0.20, reach7d: 310000, impressions7d: 720000, engagementRate: 3.8, avgLikes: 1840, avgComments: 96, avgShares: 0 },
  { id: 'page-007', client: 'FitTrack Pro', pageName: 'FitTrack Pro', platform: 'facebook', followers: 22800, followerGrowth: 284, followerGrowthPct: 1.26, reach7d: 64000, impressions7d: 148000, engagementRate: 5.6, avgLikes: 384, avgComments: 62, avgShares: 88 },
  { id: 'page-008', client: 'PetPal Supply', pageName: 'PetPal Supply', platform: 'facebook', followers: 36100, followerGrowth: 91, followerGrowthPct: 0.25, reach7d: 72000, impressions7d: 168000, engagementRate: 4.8, avgLikes: 498, avgComments: 74, avgShares: 116 },
];

const dateRanges = ['Last 7 days', 'Last 14 days', 'Last 30 days', 'Last 90 days'];

export default function PageAnalyticsContent() {
  const [selectedPageId, setSelectedPageId] = useState('page-002');
  const [dateRange, setDateRange] = useState('Last 30 days');
  const [showPageDropdown, setShowPageDropdown] = useState(false);

  const selectedPage = pages.find((p) => p.id === selectedPageId) || pages[0];

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Page Analytics</h1>
          <p className="text-sm text-muted-foreground mt-1">Monitor social page health and performance across all clients</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="input-base text-sm py-2 w-auto pr-8"
          >
            {dateRanges.map((r) => <option key={`range-${r}`} value={r}>{r}</option>)}
          </select>
          <button onClick={() => toast.success('Analytics report exported')} className="btn-ghost text-sm">
            <Download size={14} />
            Export
          </button>
        </div>
      </div>

      {/* Page Selector */}
      <div className="card-base p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Viewing:</p>
          <div className="relative">
            <button
              onClick={() => setShowPageDropdown((v) => !v)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-background hover:bg-muted text-sm font-medium transition-all duration-150"
            >
              <Badge variant={selectedPage.platform}>{selectedPage.platform === 'facebook' ? 'FB' : 'IG'}</Badge>
              <span className="text-foreground">{selectedPage.pageName}</span>
              <span className="text-xs text-muted-foreground">({selectedPage.client})</span>
              <ChevronDown size={14} className="text-muted-foreground" />
            </button>
            {showPageDropdown && (
              <div className="absolute top-full mt-1 left-0 w-80 bg-card border border-border rounded-xl shadow-modal z-20 fade-in overflow-hidden">
                {pages.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => { setSelectedPageId(p.id); setShowPageDropdown(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted transition-colors text-sm ${p.id === selectedPageId ? 'bg-primary/5' : ''}`}
                  >
                    <Badge variant={p.platform}>{p.platform === 'facebook' ? 'FB' : 'IG'}</Badge>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate">{p.pageName}</p>
                      <p className="text-xs text-muted-foreground">{p.client} · {p.followers.toLocaleString()} followers</p>
                    </div>
                    {p.id === selectedPageId && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-primary shrink-0">
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="ml-auto text-xs text-muted-foreground">
            Last updated: Today at 6:05 AM · <button className="text-primary hover:underline">Refresh</button>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <PageKPICards page={selectedPage} />

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <EngagementChart pageId={selectedPageId} platform={selectedPage.platform} />
        </div>
        <div className="lg:col-span-1">
          <AudienceDemographicsChart platform={selectedPage.platform} />
        </div>
      </div>

      {/* Top Posts */}
      <TopPostsTable pageId={selectedPageId} platform={selectedPage.platform} />
    </div>
  );
}